import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Save, User, Upload, Loader2, AlertCircle, Camera, X } from "lucide-react";
import Header from "../Header";
import AdminSidebar from "../Sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "@/firebase/config";
import { useToast } from "@/hooks/use-toast";
import imageCompression from 'browser-image-compression';

interface Teacher {
  id: string;
  employeeId: string;
  title?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  secondaryPhone?: string;
  address: string;
  dateOfBirth: string;
  nationalId: string;
  position: string[];
  tscNumber?: string;
  kraPin?: string;
  profilePicture: string | null;
  documents: string[];
  otherDocuments?: string; // Added to support form field
}

// Interface for form data to allow File types during editing
interface FormData extends Omit<Teacher, "profilePicture" | "documents"> {
  profilePicture: string | File | null;
  documents: (File | string)[];
}

interface TeacherEditFormProps {
  teacher: Teacher;
  onBack: () => void;
  onSave: (updatedTeacher: Teacher) => Promise<void> | void; // Consistent return type
}

const TeacherEditForm = ({ teacher, onBack, onSave }: TeacherEditFormProps) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    ...teacher,
    title: teacher.title || "",
    profilePicture: teacher.profilePicture,
    documents: teacher.documents || [],
    otherDocuments: teacher.otherDocuments || "",
    position: Array.isArray(teacher.position) ? teacher.position : (typeof teacher.position === 'string' && teacher.position ? [teacher.position] : []),
  });
  const [imageLoading, setImageLoading] = useState<boolean>(!!teacher.profilePicture);
  const [photoPreview, setPhotoPreview] = useState<string | null>(teacher.profilePicture);
  const profilePictureInputRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleInputChange = (field: keyof Omit<FormData, "profilePicture" | "documents" | "position">, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddPosition = (value: string) => {
    if (value && !formData.position.includes(value)) {
      setFormData(prev => ({
        ...prev,
        position: [...prev.position, value]
      }));
    }
  };

  const handleRemovePosition = (positionToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      position: prev.position.filter(p => p !== positionToRemove)
    }));
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    toast({
      title: "Processing Photo",
      description: "Optimizing teacher photo for storage...",
      duration: 2000,
    });

    const options = {
      maxSizeMB: 0.1, // ~100KB
      maxWidthOrHeight: 1280,
      useWebWorker: true,
      fileType: 'image/jpeg' as string,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      
      const fileWithNewName = new File([compressedFile], file.name, {
        type: compressedFile.type,
      });
      
      setFormData(prev => ({ ...prev, profilePicture: fileWithNewName }));
      setPhotoPreview(URL.createObjectURL(fileWithNewName));
      setImageLoading(false);
      
      toast({
        title: "Photo Optimized",
        description: `Size reduced by ${Math.round((1 - compressedFile.size / file.size) * 100)}%`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Image compression error:", error);
      setFormData(prev => ({ ...prev, profilePicture: file }));
      setPhotoPreview(URL.createObjectURL(file));
      setImageLoading(false);
      toast({
        title: "Optimization Failed",
        description: "Photo was loaded without compression.",
        variant: "destructive",
      });
    }
  };

  const triggerCamera = () => {
    if (profilePictureInputRef.current) {
      profilePictureInputRef.current.click();
    }
  };

  const handleDocumentsChange = (files: FileList | null) => {
    if (files) {
      setFormData((prev) => ({ ...prev, documents: Array.from(files) }));
    }
  };

  const cleanData = (data: any): any => {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        cleaned[key] = value;
      }
    }
    return cleaned;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.employeeId?.trim()) {
      setValidationError("Employee ID must be filled");
      return;
    }
    if (!formData.firstName?.trim()) {
      setValidationError("First Name must be filled");
      return;
    }
    if (!formData.lastName?.trim()) {
      setValidationError("Last Name must be filled");
      return;
    }
    if (!formData.email?.trim()) {
      setValidationError("Email must be filled");
      return;
    }
    if (!formData.phone?.trim()) {
      setValidationError("Phone must be filled");
      return;
    }
    if (!formData.dateOfBirth?.trim()) {
      setValidationError("Date of Birth must be filled");
      return;
    }
    if (!formData.nationalId?.trim()) {
      setValidationError("National ID must be filled");
      return;
    }
    if (formData.position.length === 0) {
      setValidationError("At least one position must be selected");
      return;
    }

    setIsSaving(true);
    try {
      // Upload profile picture if it's a File
      let profilePictureUrl: string | null = null;
      if (formData.profilePicture) {
        if (typeof formData.profilePicture === "string") {
          profilePictureUrl = formData.profilePicture;
        } else {
          const photoRef = ref(storage, `schools/teachers/${teacher.id}/profile_${formData.profilePicture.name}`);
          await uploadBytes(photoRef, formData.profilePicture);
          profilePictureUrl = await getDownloadURL(photoRef);
        }
      }

      // Upload documents if they are Files
      const documentUrls: string[] = [];
      for (const doc of formData.documents) {
        if (typeof doc === "string") {
          documentUrls.push(doc);
        } else {
          const docRef = ref(storage, `schools/teachers/${teacher.id}/docs/${doc.name}`);
          await uploadBytes(docRef, doc);
          const url = await getDownloadURL(docRef);
          documentUrls.push(url);
        }
      }

      const updatedTeacher: Teacher = {
        ...formData,
        profilePicture: profilePictureUrl,
        documents: documentUrls,
      };
      await onSave(cleanData(updatedTeacher));
    } catch (error) {
      console.error("Error saving teacher:", error);
      setValidationError("Failed to save teacher. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidebarProvider>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}</style>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <Header />
          <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-900">
            <div className="mb-6">
              <Button
                variant="outline"
                onClick={onBack}
                className="m-2 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4 text-muted-foreground" />
                Back to Teachers & Staff
              </Button>
              <h1 className="text-2xl font-bold text-black dark:text-white mb-2">Edit Teacher/Staff Information</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Modify teacher/staff details and records</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <Card className="bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-black dark:text-white">
                    <User className="h-5 w-5 text-gray-400" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Profile Picture with loader */}
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-[#1E293B] flex items-center justify-center overflow-hidden relative">
                      {formData.profilePicture ? (
                        <>
                          {imageLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-[#1E293B] z-10">
                              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                            </div>
                          )}
                          <img
                            src={
                              typeof formData.profilePicture === "string"
                                ? formData.profilePicture
                                : photoPreview || ""
                            }
                            alt="Profile"
                            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                            onLoad={() => setImageLoading(false)}
                            onError={() => setImageLoading(false)}
                          />
                        </>
                      ) : (
                        <User className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <Label htmlFor="profilePicture" className="text-black dark:text-white">
                        Profile Picture
                      </Label>
                      <div className="flex gap-2 mt-1">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={triggerCamera}
                          className="flex-1 h-10 items-center gap-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]"
                        >
                          <Camera className="h-4 w-4" />
                          Take Photo / Choose
                        </Button>
                        {formData.profilePicture && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            onClick={() => {
                              setFormData(prev => ({ ...prev, profilePicture: null }));
                              setPhotoPreview(null);
                              if (profilePictureInputRef.current) profilePictureInputRef.current.value = "";
                            }}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <input
                        id="profilePicture"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        ref={profilePictureInputRef}
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Photos are optimized to save space while maintaining quality.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="employeeId" className="text-black dark:text-white">
                        Employee ID
                      </Label>
                      <Input
                        id="employeeId"
                        value={formData.employeeId}
                        onChange={(e) => handleInputChange("employeeId", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nationalId" className="text-black dark:text-white">
                        National ID
                      </Label>
                      <Input
                        id="nationalId"
                        value={formData.nationalId}
                        onChange={(e) => handleInputChange("nationalId", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="title" className="text-black dark:text-white">
                        Title
                      </Label>
                      <Select
                        value={formData.title || ""}
                        onValueChange={(value) => handleInputChange("title" as keyof Omit<FormData, "profilePicture" | "documents" | "position">, value)}
                      >
                        <SelectTrigger className="h-10 rounded-md bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                          <SelectValue placeholder="Select title" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                          <SelectItem value="Mr">Mr</SelectItem>
                          <SelectItem value="Mrs">Mrs</SelectItem>
                          <SelectItem value="Miss">Miss</SelectItem>
                          <SelectItem value="Ms">Ms</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="firstName" className="text-black dark:text-white">
                        First Name
                      </Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange("firstName", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="middleName" className="text-black dark:text-white">
                        Middle Name <span className="text-gray-400 text-xs">(Optional)</span>
                      </Label>
                      <Input
                        id="middleName"
                        value={formData.middleName || ""}
                        onChange={(e) => handleInputChange("middleName", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-black dark:text-white">
                        Last Name
                      </Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange("lastName", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dateOfBirth" className="text-black dark:text-white">
                        Date of Birth
                      </Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-black dark:text-white">
                        Position(s)
                      </Label>
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.position.map((pos) => (
                            <Badge key={pos} variant="secondary" className="pl-2 pr-1 py-1 flex items-center gap-1">
                              {pos.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                              <button
                                type="button"
                                onClick={() => handleRemovePosition(pos)}
                                className="hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full p-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                        <Select onValueChange={handleAddPosition}>
                          <SelectTrigger className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                            <SelectValue placeholder="Add position..." />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            <SelectItem value="teacher">Full-Time Teacher</SelectItem>
                            <SelectItem value="contract_teacher">Contract Teacher</SelectItem>
                            <SelectItem value="teacher_on_practice">Teacher on Practice</SelectItem>
                            <SelectItem value="librarian">Librarian</SelectItem>
                            <SelectItem value="lab_technician">Lab Technician</SelectItem>
                            <SelectItem value="bursar">Bursar</SelectItem>
                            <SelectItem value="head_of_department">Head of Department</SelectItem>
                            <SelectItem value="deputy_principal">Deputy Principal</SelectItem>
                            <SelectItem value="principal">Principal</SelectItem>
                            <SelectItem value="other_staff">Other Staff</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-black dark:text-white">
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-black dark:text-white">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="secondaryPhone" className="text-black dark:text-white">
                        Secondary Phone <span className="text-gray-400 text-xs">(Optional)</span>
                      </Label>
                      <Input
                        id="secondaryPhone"
                        value={formData.secondaryPhone || ""}
                        onChange={(e) => handleInputChange("secondaryPhone", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div></div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-black dark:text-white">
                      Physical Address
                    </Label>
                    <Textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      rows={3}
                      className="rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tscNumber" className="text-black dark:text-white">
                        TSC Number <span className="text-gray-400 text-xs">(Optional)</span>
                      </Label>
                      <Input
                        id="tscNumber"
                        value={formData.tscNumber || ""}
                        onChange={(e) => handleInputChange("tscNumber", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="kraPin" className="text-black dark:text-white">
                        KRA PIN <span className="text-gray-400 text-xs">(Optional)</span>
                      </Label>
                      <Input
                        id="kraPin"
                        value={formData.kraPin || ""}
                        onChange={(e) => handleInputChange("kraPin", e.target.value)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="otherDocuments" className="text-black dark:text-white">
                      Other Documents Description <span className="text-gray-400 text-xs">(Optional)</span>
                    </Label>
                    <Textarea
                      id="otherDocuments"
                      value={formData.otherDocuments || ""}
                      onChange={(e) => handleInputChange("otherDocuments", e.target.value)}
                      rows={3}
                      className="rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="documents" className="text-black dark:text-white">
                      Upload Documents <span className="text-gray-400 text-xs">(Optional)</span>
                    </Label>
                    <div className="flex items-center gap-2">
                       <Input
                        id="documents"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentsChange(e.target.files)}
                        className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white"
                      />
                      <Upload className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end space-x-4 pt-6 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  className="m-2 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="m-2 h-10 bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>

            <Dialog open={!!validationError} onOpenChange={() => setValidationError(null)}>
              <DialogContent className="sm:max-w-[400px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                    <AlertCircle className="h-5 w-5" />
                    Required Field Missing
                  </DialogTitle>
                </DialogHeader>
                <div className="py-4">
                  <p className="text-center text-gray-700 dark:text-gray-300 text-lg">
                    {validationError}
                  </p>
                </div>
                <div className="flex justify-center">
                  <Button
                    onClick={() => setValidationError(null)}
                    className="h-10 bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB]"
                  >
                    OK
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default TeacherEditForm;